import { YieldBearingAssetDepositResponseInterface } from '@/components/organisms/YieldBearingAssetInvestmentActions/interfaces/YieldBearingAssetActionsInterfaces';
import { useWalletsContext } from '@/components/providers/postauth/UserWalletsProvider';
import APP_CONFIG from '@/config/app';
import { TokenInterface, WalletTokenInterface } from '@/interfaces';
import { WalletInterface } from '@/interfaces/WalletInterface';
import { YieldBearingAssetInterface } from '@/interfaces/YieldBearingAssetInterface';
import { fetchDefiLlamaYieldHistoricalData } from '@/services/DeFiLlamaService';
import fetch, {
  deposit,
  getDepositEstimate,
  optimizePortfolio,
} from '@/services/YieldBearingAssets';
import { formatRiskOrReturnScoreToDescription } from '@/utils/formatUtils';
import {
  aggregateYieldDataForAssetsByTimeInterval,
  calculateAggregatedTimeSeriesYieldData,
  calculateEffectiveApy,
  generateTokenId,
  preAggregateData,
} from '@/utils/functionalUtils';
import { logError } from '@/utils/logger';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAuthorization } from './AuthorizationProvider';
type YieldBearingAssetRawDataContextType = {
  yieldBearingAssets: YieldBearingAssetInterface[];
  loading: boolean;
  initialLoading: boolean;
  fetching: boolean;
  error: Error | null;
  getAssetYieldData: (
    defiLlamaIdOrTicker: string,
    isTicker?: boolean,
    aggregationTimeInterval?: 'daily' | 'weekly' | 'monthly',
  ) => Promise<YieldData | null>;
  fetchAssetHistoricalData: (assetId: string) => Promise<any[] | null>;
};
const YieldBearingAssetRawDataContext =
  createContext<YieldBearingAssetRawDataContextType>(
    {} as YieldBearingAssetRawDataContextType,
  );
type YieldBearingAssetsContextType = {
  yieldBearingAssetsFound: boolean;
  yieldBearingAssets: YieldBearingAssetInterface[];
  loading: boolean;
  initialLoading: boolean;
  fetching: boolean;
  error: Error | null;
  isUserAuthorized: boolean;
  deposit: (
    yieldBearingAssetTicker: string,
    yieldBearingNativeAssets: Array<{ id: string; composition: number }>,
    token: { symbol: string; address: string; blockchain: string },
    amount: string,
  ) => Promise<YieldBearingAssetDepositResponseInterface>;
  getDepositFee: (
    yieldBearingAssetTicker: string,
    yieldBearingNativeAssets: Array<{ id: string; composition: number }>,
    token: { symbol: string; address: string; blockchain: string },
    amount: string,
  ) => Promise<{
    convenienceFee: string;
    convenienceFeeInUsd: string;
    gasCost: string;
    gasCostInUsd: string;
    bridgeFee: string;
    bridgeFeeInUsd: string;
    totalFee: string;
    totalFeeInUsd: string;
  } | null>;
  optimizePortfolio: (
    assetsWithData: Array<{
      name: string;
      timeSeriesData: Array<{ timestamp: number; apy: number; tvlUsd: number }>;
    }>,
    assessment: Array<{ question: string; rating: number }>,
    userCommentary?: string,
  ) => Promise<{ allocations: number[]; explanation: string }>;
};
interface YieldData {
  timeSeriesData: Array<any>;
}
const YieldBearingAssetsContext = createContext<YieldBearingAssetsContextType>(
  {} as YieldBearingAssetsContextType,
);
export const useYieldBearingAssets = () =>
  useContext(YieldBearingAssetsContext);
export const useYieldBearingAssetRawData = () =>
  useContext(YieldBearingAssetRawDataContext);
interface YieldBearingAssetsProviderProps {
  children: ReactNode;
  staleTime?: number;
  retry?: number;
}
const getEligibleTokensForDeposit = (
  wallets: WalletInterface[],
): WalletTokenInterface[] => {
  return wallets
    .flatMap((wallet) => wallet.balances.filter((token) => token.balance > 0))
    .map(generateTokenId);
};
const checkIfTokenInWallet = (
  token: TokenInterface,
  wallets: WalletInterface[],
): WalletTokenInterface | TokenInterface => {
  for (const wallet of wallets) {
    const walletToken = wallet.balances.find(
      (t) => t.symbol === token.symbol && t.blockchain === token.blockchain,
    );
    if (walletToken) {
      return {
        ...token,
        ...walletToken,
      };
    }
  }
  return token;
};
const getEmojiRating = (averageRating: number | undefined): string => {
  if (!averageRating) return APP_CONFIG.RATING_EMOJIS[0];
  if (averageRating >= 4.5) return APP_CONFIG.RATING_EMOJIS[4];
  if (averageRating >= 3.5) return APP_CONFIG.RATING_EMOJIS[3];
  if (averageRating >= 2.5) return APP_CONFIG.RATING_EMOJIS[2];
  if (averageRating >= 1.5) return APP_CONFIG.RATING_EMOJIS[1];
  return APP_CONFIG.RATING_EMOJIS[0];
};
const YieldBearingAssetRawDataProvider = ({
  children,
  staleTime,
  retry,
}: YieldBearingAssetsProviderProps) => {
  const queryClient = useQueryClient();
  const [initialLoading, setInitialLoading] = useState(true);
  const {
    data = [],
    error,
    isLoading,
  } = useQuery<YieldBearingAssetInterface[], Error>({
    queryKey: ['yieldBearingAssets'],
    queryFn: fetch,
    staleTime: staleTime,
    retry: retry,
  });
  useEffect(() => {
    if (isLoading && (!data || data.length === 0)) {
      setInitialLoading(true);
    } else if (data?.length > 0) {
      setInitialLoading(false);
    }
  }, [isLoading, data]);
  const getAssetYieldData = useCallback(
    async (
      defiLlamaIdOrTicker: string,
      isTicker = false,
      aggregationTimeInterval: 'daily' | 'weekly' | 'monthly' = 'daily',
    ): Promise<YieldData | null> => {
      const queryKey = [
        'assetYieldData',
        defiLlamaIdOrTicker,
        aggregationTimeInterval,
      ];
      try {
        if (!isTicker) {
          const { timeSeriesData } = await queryClient.fetchQuery({
            queryKey,
            queryFn: () =>
              fetchDefiLlamaYieldHistoricalData(defiLlamaIdOrTicker),
            staleTime: staleTime,
            retry: retry,
          });
          const preAggregatedData =
            aggregationTimeInterval !== 'daily'
              ? preAggregateData(timeSeriesData, aggregationTimeInterval)
              : timeSeriesData;
          return { timeSeriesData: preAggregatedData };
        } else {
          const asset = queryClient
            .getQueryData<YieldBearingAssetInterface[]>(['yieldBearingAssets'])
            ?.find((a) => a.ticker === defiLlamaIdOrTicker);
          if (!asset) return null;
          const underlyingAssetPromises =
            asset.underlyingYieldBearingAssets.map(async (underlyingAsset) => {
              const yieldData = await getAssetYieldData(
                underlyingAsset.defiLlamaId,
                false,
                aggregationTimeInterval,
              );
              return {
                id: underlyingAsset.defiLlamaId,
                timeSeriesData: yieldData?.timeSeriesData || [],
                composition: underlyingAsset.composition,
              };
            });
          const underlyingAssetData = await Promise.all(
            underlyingAssetPromises,
          );
          const aggregatedData = aggregateYieldDataForAssetsByTimeInterval(
            underlyingAssetData,
            underlyingAssetData.map((item) => ({
              composition: item.composition,
            })),
            aggregationTimeInterval,
          );
          return { timeSeriesData: aggregatedData };
        }
      } catch (error) {
        logError(error, 'YieldBearingAssetsProvider.getAssetYieldData');
        return null;
      }
    },
    [queryClient, staleTime, retry],
  );
  const fetchAssetHistoricalData = useCallback(
    async (assetId: string): Promise<any[] | null> => {
      try {
        const asset = data?.find((a) => a.ticker === assetId);
        if (!asset) return null;
        const underlyingAssetPromises = asset.underlyingYieldBearingAssets.map(
          async (underlyingAsset) => {
            const yieldData = await getAssetYieldData(
              underlyingAsset.defiLlamaId,
              false,
              'daily',
            );
            return {
              id: underlyingAsset.defiLlamaId,
              timeSeriesData: yieldData?.timeSeriesData || [],
              composition: underlyingAsset.composition,
            };
          },
        );
        const underlyingAssetData = await Promise.all(underlyingAssetPromises);
        const aggregatedData = calculateAggregatedTimeSeriesYieldData(
          underlyingAssetData,
          underlyingAssetData.map((item) => ({
            composition: item.composition,
          })),
          0,
        );
        return aggregatedData;
      } catch (error) {
        logError(
          error,
          'YieldBearingAssetRawDataProvider.fetchAssetHistoricalData',
        );
        return null;
      }
    },
    [data, getAssetYieldData],
  );
  const value = useMemo(
    () => ({
      yieldBearingAssets: data,
      loading: isLoading,
      initialLoading,
      fetching: isLoading && !!data?.length,
      error: error || null,
      getAssetYieldData,
      fetchAssetHistoricalData,
    }),
    [
      data,
      isLoading,
      initialLoading,
      error,
      getAssetYieldData,
      fetchAssetHistoricalData,
    ],
  );
  return (
    <YieldBearingAssetRawDataContext.Provider value={value}>
      {children}
    </YieldBearingAssetRawDataContext.Provider>
  );
};
const YieldBearingAssetsEnhancer = ({ children }: { children: ReactNode }) => {
  const {
    yieldBearingAssets: rawData,
    loading,
    initialLoading,
    fetching,
    error,
  } = useYieldBearingAssetRawData();
  const { isUserAuthorized } = useAuthorization();
  const { wallets } = useWalletsContext();
  const [historicalDataCache, setHistoricalDataCache] = useState<
    Record<string, any[]>
  >({});
  const previousData = useRef<YieldBearingAssetInterface[] | null>(null);
  useEffect(() => {
    if (!loading && rawData?.length) {
      previousData.current = rawData;
    }
  }, [loading, rawData]);
  const enhancedData = useMemo(() => {
    return (
      rawData
        ?.filter(
          (asset) =>
            !APP_CONFIG.YIELD_BEARING_ASSET_TICKERS_EXCLUSION_LIST.includes(
              asset.ticker,
            ),
        )
        .map((asset) => {
          const effectiveApy = calculateEffectiveApy(
            asset.underlyingYieldBearingAssets.map((underlyingAsset) => ({
              apy: underlyingAsset.apy,
              tvl: underlyingAsset.tvlUSD || 0,
              composition: underlyingAsset.composition,
            })),
          );
          const tokensSupportedForDeposit = Array.isArray(
            asset.tokensSupportedForDeposit,
          )
            ? asset.tokensSupportedForDeposit.map((token) =>
                generateTokenId(checkIfTokenInWallet(token, wallets)),
              )
            : [];
          const tokensSupportedForWithdrawal =
            asset.tokensSupportedForWithdrawal.map((token) =>
              generateTokenId(checkIfTokenInWallet(token, wallets)),
            );
          const underlyingTokens = asset.underlyingTokens?.map((token) =>
            generateTokenId(checkIfTokenInWallet(token, wallets)),
          );
          return {
            ...asset,
            apy: effectiveApy,
            tokensSupportedForDeposit,
            tokensSupportedForWithdrawal,
            underlyingTokens,
            emojiRating: asset.ratings?.overallMetrics
              ? getEmojiRating(asset.ratings.overallMetrics.averageRating)
              : APP_CONFIG.RATING_EMOJIS[0],
            riskType: formatRiskOrReturnScoreToDescription(asset.riskScore),
            returnType: formatRiskOrReturnScoreToDescription(asset.returnScore),
          };
        }) || previousData.current
    );
  }, [rawData, wallets]);
  const queryClient = useQueryClient();
  const { getAssetYieldData } = useYieldBearingAssetRawData();
  const handleDeposit = async (
    yieldBearingAssetTicker: string,
    yieldBearingNativeAssets: Array<{ id: string; composition: number }>,
    token: { symbol: string; address: string; blockchain: string },
    amount: string,
  ): Promise<YieldBearingAssetDepositResponseInterface> => {
    if (
      !yieldBearingNativeAssets.every(
        (asset) => asset.id && asset.composition >= 0,
      )
    ) {
      logError('Invalid yieldBearingNativeAssets format');
      throw new Error('Invalid format for yield-bearing assets');
    }
    if (!token.symbol || !token.address || !token.blockchain) {
      logError('Invalid token format');
      throw new Error('Invalid format for deposit token');
    }
    try {
      const response = await deposit(
        yieldBearingAssetTicker,
        yieldBearingNativeAssets,
        token,
        amount,
      );
      const result: YieldBearingAssetDepositResponseInterface = {
        amount: response.amount,
        amountInUsd: response.amountInUsd,
        gasCost: response.gasCost,
        gasCostInUsd: response.gasCostInUsd,
        bridgeFee: response.bridgeFee || '0',
        bridgeFeeInUsd: response.bridgeFeeInUsd || '0',
        sliverFee: response.sliverFee,
        sliverFeeInUsd: response.sliverFeeInUsd,
      };
      queryClient.invalidateQueries({
        queryKey: ['yieldBearingAssets'],
        refetchType: 'all',
      });
      return result;
    } catch (error) {
      logError(error, 'YieldBearingAssetsProvider.handleDeposit');
      throw error;
    }
  };
  const getDepositFee = async (
    yieldBearingAssetTicker: string,
    yieldBearingNativeAssets: Array<{ id: string; composition: number }>,
    token: { symbol: string; address: string; blockchain: string },
    amount: string,
  ): Promise<{
    convenienceFee: string;
    convenienceFeeInUsd: string;
    gasCost: string;
    gasCostInUsd: string;
    bridgeFee: string;
    bridgeFeeInUsd: string;
    totalFee: string;
    totalFeeInUsd: string;
  } | null> => {
    try {
      if (!yieldBearingNativeAssets.length) {
        throw new Error('No underlying yield-bearing assets provided.');
      }
      if (!token.address || !token.blockchain) {
        throw new Error('Invalid token details for deposit.');
      }
      const response = await getDepositEstimate(
        yieldBearingAssetTicker,
        yieldBearingNativeAssets.map((asset) => ({
          id: asset.id,
          composition: asset.composition,
        })),
        {
          address: token.address,
          blockchain: token.blockchain,
        },
        amount,
      );
      return {
        convenienceFee: response.sliverFee,
        convenienceFeeInUsd: response.sliverFeeInUsd,
        gasCost: response.gasCost,
        gasCostInUsd: response.gasCostInUsd,
        bridgeFee: response.bridgeFee || '0',
        bridgeFeeInUsd: response.bridgeFeeInUsd || '0',
        totalFee: response.totalFee,
        totalFeeInUsd: response.totalFeeInUsd,
      };
    } catch (error) {
      logError(error, 'YieldBearingAssetsProvider.getDepositFee');
      throw error;
    }
  };
  const value = useMemo(
    () => ({
      yieldBearingAssetsFound: !!enhancedData?.length,
      yieldBearingAssets: enhancedData || [],
      loading,
      initialLoading,
      fetching,
      error,
      isUserAuthorized,
      deposit: handleDeposit,
      getDepositFee,
      optimizePortfolio,
    }),
    [enhancedData, loading, initialLoading, fetching, error, isUserAuthorized],
  );
  return (
    <YieldBearingAssetsContext.Provider value={value}>
      {children}
    </YieldBearingAssetsContext.Provider>
  );
};
export const YieldBearingAssetsProvider = ({
  children,
  staleTime = 0.5 * 60 * 60 * 1000,
  retry = 5,
}: YieldBearingAssetsProviderProps): JSX.Element => {
  return (
    <YieldBearingAssetRawDataProvider staleTime={staleTime} retry={retry}>
      <YieldBearingAssetsEnhancer>{children}</YieldBearingAssetsEnhancer>
    </YieldBearingAssetRawDataProvider>
  );
};
export const useRefetchYieldBearingAssets = () => {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({
      queryKey: ['yieldBearingAssets'],
      refetchType: 'all',
    });
  };
};
